import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'

import App from '@/App.vue'
import { router } from '@/router'
import { queryClient } from '@/api/queries'
import { useThemeStore } from '@/stores/theme'
import '@/assets/main.css'

async function bootstrap(): Promise<void> {
  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  app.use(router)
  app.use(VueQueryPlugin, { queryClient })

  // Kick the theme store once so its `watch(resolved, { immediate: true })`
  // applies the persisted `data-theme` to <html> before first paint.
  useThemeStore()

  // Sentry is gated behind a DSN env var so local dev stays quiet. UI Spec §4.5.
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (dsn) {
    try {
      const Sentry = await import('@sentry/vue')
      Sentry.init({
        app,
        dsn,
        environment: import.meta.env.VITE_APP_ENV ?? 'local',
        release: import.meta.env.VITE_RELEASE,
        tracesSampleRate: 0.1,
        // Session replay is consent-gated for admin surfaces — wire up via a toggle,
        // not unconditionally. Tech Stack §4.5.
        integrations: [],
      })
    } catch (err) {
      // Sentry is optional; if it fails to load we still want the app up.
      // eslint-disable-next-line no-console
      console.warn('[sentry] init skipped:', err)
    }
  }

  app.mount('#app')
}

void bootstrap()
